import React, { FC, useCallback, useEffect, useMemo, useState, useRef, memo } from "react"
import SyncableModel from "../../../../database/SyncableModel"
import { Appearance, ViewStyle, ScrollView } from "react-native"
import { Box, Icon, View } from "native-base"
import { Button, CycleDiseaseForm, DeleteModal, JalaIcon, ModelCreateScreen, Text } from "../../../../components"
import { CompositeScreenProps, useIsFocused } from "@react-navigation/native"
import { CycleDisease, File } from "../../../../database"
import { color, nativeBaseTheme } from "../../../../theme"
import { navigate } from "../../../../navigators"
import { observer } from "mobx-react-lite"
import { useStores } from "../../../../models"
import { exitWalkthrough, startWalkthrough } from "react-native-walkthrough"
import formWalkthrough from "../../../../guides/form-walkthrough"
import { translate } from "../../../../i18n"

const DELETE_BUTTON: ViewStyle = {
  alignSelf: "flex-end",
  marginVertical: 8,
  marginHorizontal: 16
}

const FormWalkthrough: FC<{
  scrollViewRef?: { current: ScrollView }
}> = memo(observer(function FormWalkthrough(props) {
  const { scrollViewRef } = props
  const { userStore } = useStores()
  const { user } = userStore
  const isFocused = useIsFocused()

  useEffect(() => {
    if (!isFocused || !user || user?.state?.add_disease_coachmark_shown) return undefined
    startWalkthrough(formWalkthrough(
      [
        {
          id: 'cycle-disease-form-first',
          content: translate("cycleDiseaseForm.diseaseTooltip"),
          placement: 'top',
          tooltipProps: {},
        },
        {
          id: 'cycle-disease-form-second',
          content: translate("cycleDiseaseForm.diseaseEvidence"),
          placement: 'top',
          tooltipProps: {},
        }
      ], () => {
        userStore.updateData({ state: { add_disease_coachmark_shown: true } })
          .finally(() => userStore.getUser())
        if (scrollViewRef && scrollViewRef.current)
          return scrollViewRef.current.scrollTo({ y: 0 })
      },
    ))
    return () => isFocused && exitWalkthrough();
  }, [isFocused, scrollViewRef, user, userStore])

  return null
}))

export const CycleDiseaseCreateScreen: FC<{ colorScheme?: "light" | "dark", route?: any } & CompositeScreenProps<any, any>> = observer(function CycleDiseaseInputScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), route: { params = {} } } = props

  // Pull in one of our MST stores
  const {
    appStore: { farmId, farmStore },
    cycleDiseaseStore: modelStore,
  } = useStores();

  const [ model, setModel ] = useState<SyncableModel>(null)
  const scrollViewRef = useRef<ScrollView>(null)

  const initialValues = useMemo<any>(() => {
    const data = (model ? model.toArray() : params)
    return {
      farm_id: farmId,
      ...data,
    }
  }, [farmId, model, params])

  useEffect(() => {
    const cycleDisease = farmStore.cycleDiseases.find((m) => m.id === params.id);
    if (cycleDisease) {
      const { files, ...restCycleDisease } = cycleDisease
      const model = CycleDisease.hydrate({ ...restCycleDisease });
      model.setRelation('files', (files || []).map((file) => File.hydrate({ ...file })))
      setModel(model)
    }
  }, [farmStore, params.id, setModel])

  const handleDisease = useCallback(() => (
    navigate('Home.Stack', 'Home.Disease')
  ), [])

  const renderDeleteButton = useCallback(({ model, colorScheme, loading, handleDelete }) => (
    <View flexDirection="row" justifyContent="space-between" alignItems="center" >
      <Button
        testID="jala:id/disease-button"
        preset="link"
        colorScheme={colorScheme}
        onPress={handleDisease}
        style={DELETE_BUTTON}
      >
        <View flexDirection="row">
          <Box marginRight="8px" size="20px" borderRadius="4px" bg={color.primary} alignItems="center" justifyContent="center">
            <Icon as={JalaIcon} name="diseases" color={color.palette.white} size="14px" />
          </Box>
          <Text
            tx="cycleDiseaseCreateScreen.seeDiseaseInfo"
            preset="title4"
            colorScheme={colorScheme}
            color={color.primary}
          />
        </View>
      </Button>
      {model && <DeleteModal
        testID="jala:id/delete-button"
        style={DELETE_BUTTON}
        onConfirm={handleDelete}
        disabled={loading}
      >
        <Text
          tx="delete"
          preset="title4"
          colorScheme={colorScheme}
          color={loading ? nativeBaseTheme.colors.muted["500"] : nativeBaseTheme.colors.danger["500"]}
        />
      </DeleteModal>}
    </View>
  ), [handleDisease])

  return (<>
    <ModelCreateScreen
      colorScheme={colorScheme}
      ModelForm={CycleDiseaseForm}
      initialValues={initialValues}
      modelStore={modelStore}
      model={model}
      renderDeleteButton={renderDeleteButton}
      hideOnBlurred={false}
      scrollViewRef={scrollViewRef}
    />
    <FormWalkthrough scrollViewRef={scrollViewRef} />
  </>)
})
